/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { call, delay, put, race } from "redux-saga/effects";

import * as EventConstants from "../../Constants/EventConstants";
import * as eventApi from "../../Services/EventApi";

import { GENERIC_API_TIMEOUT_ERROR } from "../../Constants/ApplicationConstants";
import { HttpStatusCode } from "../../Constants/HttpStatusCode";
import { IEvent } from "../../Interfaces/Event/IEvent";
import { IEventDataResult } from "../../Interfaces/Event/IEventDataResult";
import { IEventsDataResult } from "../../Interfaces/Event/IEventsDataResult";
import { IEventsRequest } from "../../Interfaces/Event/IEventsRequest";
import { IEventsResult } from "../../Interfaces/Event/IEventsResult";
import { IEventUpdateStatusDataResult } from "../../Interfaces/Event/IEventUpdateStatusDataResult";
import { IApiErrorResult, IApiResult2 } from "../../Interfaces/IApiResult";
import { getDefaultApiTimeOutConfiguration } from "../../Utility/Utils";
import {
  loadEventFail,
  loadEventSuccess,
  searchEventsFail,
  searchEventsSuccess,
  updateEventStatusFail,
  updateEventStatusSuccess,
  ILoadEvent,
  ISearchEvents,
  IUpdateEventStatus,
} from "./EventAction";

/**
 * Load an individual events
 * @param {ILoadEvent} action for loading an event
 * @returns {void}
 */
function* handleLoadEvent(action: ILoadEvent) {
  const failedMessage = "Failed to load the selected event.";
  const { selectedEventId } = action;

  try {
    const { result, timeout }: IApiResult2<IEventDataResult> | any = yield race({
      result: call(eventApi.getEvent, selectedEventId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadEventFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const event = result.parsedBody as IEvent;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(loadEventSuccess(event));
        break;
      case HttpStatusCode.BadRequest:
        yield put(loadEventFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(loadEventFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(loadEventFail(error));
  }
}

/**
 * Search all events for given text
 * @param {ISearchEvents} action for searching events
 * @returns {void}
 */
function* handleSearchEvents(action: ISearchEvents) {
  const failedMessage = "Failed to search the events.";
  const requestBody = {
    searchKey: action.searchText,
    pageIndex: action.searchPage,
    pageSize: action.pageSize,
    eventsStatus: [],
    eventsType: [],
    eventsFormat: [],
    eventsCountry: [],
    eventsState: [],
    eventsLastReviewedBy: [],
    eventsStudentMilestone: [],
    eventsStartDate: undefined,
    eventsEndDate: undefined,
  } as unknown as IEventsRequest;

  action.selectedEventsFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case EventConstants.EVENT_STATUS:
        requestBody.eventsStatus.push(item.value);
        break;
      case EventConstants.EVENT_TYPE:
        requestBody.eventsType.push(item.value);
        break;
      case EventConstants.EVENT_FORMAT:
        requestBody.eventsFormat.push(item.value);
        break;
      case EventConstants.EVENT_COUNTRY_REGION:
        requestBody.eventsCountry.push(item.value);
        break;
      case EventConstants.EVENT_STATE_PROVINCE:
        requestBody.eventsState.push(item.value);
        break;
      case EventConstants.EVENT_LAST_REVIEWED_BY:
        requestBody.eventsLastReviewedBy.push(item.value);
        break;
      case EventConstants.EVENT_STUDENT_MILESTONE:
        requestBody.eventsStudentMilestone.push(item.value);
        break;
      case EventConstants.EVENT_START_DATE:
        requestBody.eventsStartDate = new Date(item.value);
        break;
      case EventConstants.EVENT_END_DATE:
        requestBody.eventsEndDate = new Date(item.value);
        break;
    }
  });

  try {
    const { result, timeout }: IApiResult2<IEventsDataResult> | any = yield race({
      result: call(eventApi.searchEvents, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchEventsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const parsedBody = result.parsedBody as IEventsResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(
          searchEventsSuccess(
            parsedBody.events,
            action.searchPage,
            action.pageSize,
            parsedBody.filteredCount
          )
        );
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchEventsFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchEventsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(searchEventsFail(error));
  }
}

/**
 * Update an event status
 * @param {IUpdateEventStatus} action for updating an event's status
 * @returns {void}
 */
function* handleEventStatusUpdate(action: IUpdateEventStatus) {
  const failedMessage = "Failed to update the event's status.";
  const { eventStatus } = action;

  try {
    const { result, timeout }: IApiResult2<IEventUpdateStatusDataResult> | any = yield race({
      result: call(eventApi.updateEventStatus, eventStatus),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateEventStatusFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const event = result.parsedBody as IEvent;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(updateEventStatusSuccess(event));
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateEventStatusFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateEventStatusFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateEventStatusFail(error));
  }
}

export { handleLoadEvent, handleSearchEvents, handleEventStatusUpdate };
